// ============================================================
// How a row of badges folds (plan plate-unboxed): which items fit one line
// and where the "+N" stands. Pure arithmetic over measured edges, so the
// rule is testable without a layout engine.
// ============================================================

/** Room the "+N" button takes at the end of the line, its gap included, in px. */
export const MORE_ROOM = 36;

/** The gap between items, in px (the row's CSS gap). */
export const ITEM_GAP = 4;

export interface Fold {
  /** How many items, from the first, stay on the line. */
  shown: number;
  /** How many fold into the "+N". */
  hidden: number;
  /** Where the "+N" stands, from the line's left edge, in px. */
  at: number;
}

/**
 * The fold for items whose right edges are `edges` (left to right, px from the line's left) on a line `width` px
 * wide: null when every item fits; otherwise as many as fit beside the "+N", the rest folded.
 */
export function foldOf(edges: readonly number[], width: number): Fold | null {
  // The line's width is whole pixels and the edges are not: a badge within a pixel of it fits (2026-10-24 - a lone
  // condition chip measured 87.4 on a line of 87 and folded away, leaving "+1").
  if (edges.length === 0 || edges[edges.length - 1] < width + 1) return null;
  const shown = edges.filter((edge) => edge <= width - MORE_ROOM).length;
  return { shown, hidden: edges.length - shown, at: shown ? edges[shown - 1] + ITEM_GAP : 0 };
}

/** Two folds the same (a resize that changes nothing must not re-render). Plain data, so its JSON is its identity. */
export const sameFold = (a: Fold | null, b: Fold | null) => JSON.stringify(a) === JSON.stringify(b);
